import { createSlice } from '@reduxjs/toolkit';
import { BalanceTransactionDetails } from 'types/wallet';
import { notify } from 'components/Common/Toast';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';

export enum MONEY_CONTAINER_TYPE {
    BANK_ACCOUNT = 'BANK_ACCOUNT',
    CC = 'CC',
    SVA = 'SVA',
    LOYALTY = 'LOYALTY',
}

export const getAttribute = (moneyContainer: any, key: 'attributes' | 'attributesSecureRetrievable', attr: string) => {
    if (!moneyContainer?.[key]) return '';
    return moneyContainer[key]?.find((a: any) => a.key === attr)?.value || '';
};

export const getLabel = (moneyContainer: MoneyContainer, separator: string = '-') => {
    if (!moneyContainer?.type) return '';
    let name;
    switch (moneyContainer.type) {
        case MONEY_CONTAINER_TYPE.BANK_ACCOUNT:
            name = getAttribute(moneyContainer, 'attributes', 'INSTITUTION_NAME');
            break;
        case MONEY_CONTAINER_TYPE.CC:
        case MONEY_CONTAINER_TYPE.SVA:
            name = getAttribute(moneyContainer, 'attributes', 'ACCOUNT_NAME');
            break;
        default:
            name = '';
            break;
    }
    return `${name} ${separator || '-'} ${moneyContainer.last4AccountDigits}`;
};

/** Phase 12 — static Freeze: SVA / executeTrx removed; no legacy network. */
export const withdrawToBank = createAsyncThunkWrapper<any, BalanceTransactionDetails>('wallet/withdrawToBank', async () => {
    notify('Withdraw is unavailable until payments remount.', 'info');
    return null;
});

interface Attribute {
    key: string;
    value: string;
}

interface SecureRetrievableAttribute {
    key: string;
    value: string;
}

interface MoneyContainer {
    personId?: string;
    last4AccountDigits: string;
    currency: string;
    status: string;
    createdDate: string;
    lastUpdatedDate: string;
    mozidoId: number;
    containerId: number;
    type: string;
    subType?: string;
    description?: string;
    attributes: Attribute[];
    attributesSecureRetrievable?: SecureRetrievableAttribute[];
    default?: boolean;
}

interface MoneyContainerState {
    records: MoneyContainer[];
    defaultSVA: MoneyContainer;
    error?: string | undefined;
    loading: boolean;
}
const sva: MoneyContainer = {
    personId: '',
    last4AccountDigits: '',
    currency: '840',
    status: '',
    createdDate: '',
    lastUpdatedDate: '',
    mozidoId: 0,
    containerId: 0,
    type: 'SVA',
    attributes: [
        {
            key: 'ACCOUNT_NAME',
            value: '',
        },
    ],
    default: false,
};

const initialState: MoneyContainerState = {
    records: [],
    defaultSVA: sva,
    loading: false,
};

/** Phase 12 — static: empty containers (Nest has no SVA). */
export const fetchMoneyContainer = createAsyncThunkWrapper('wallet/fetchMoneyContainer', async () => {
    return { moneyContainers: [] as MoneyContainer[], defaultSVA: sva };
});

export const getDefaultSvaOfTheMerchant = createAsyncThunkWrapper('wallet/getDefaultSVAOfTheMerchant', async () => {
    return sva;
});

const moneyContainerSlice = createSlice({
    name: 'moneyContainerSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchMoneyContainer.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(fetchMoneyContainer.fulfilled, (state, action) => {
            state.loading = false;
            const payload = action?.payload;
            if (!payload) return;
            const { moneyContainers, defaultSVA } = payload || {};
            state.records = (moneyContainers || []).map((mc: any) => ({ ...mc, label: getLabel(mc) }));
            if (defaultSVA) {
                state.defaultSVA = defaultSVA;
            }
        });
        builder.addCase(fetchMoneyContainer.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });
    },
});

export default moneyContainerSlice.reducer;
